import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Button } from '@ludolio/shared-ui';
import { Badge } from '@ludolio/shared-ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ludolio/shared-ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ludolio/shared-ui';
import { Trophy, Calendar, Clock } from 'lucide-react';
import type { User, UserAchievement, Assignment, Unit } from '@/types';
import { StudentsDataTable } from './StudentsDataTable';
import { useStudentStore } from '@/stores/studentStore';
import { apiClient } from '@/services/api';

interface StudentPlayingTime {
  gameId: string;
  gameTitle: string;
  totalMinutes: number;
  sessionCount: number;
  lastPlayedAt: string | null;
}

const SEMESTER_LABELS: Record<Unit['semester'], string> = {
  semester_1: 'Semester 1',
  semester_2: 'Semester 2',
  summer: 'Summer',
  winter: 'Winter',
};

export function StudentsView() {
  const { students, fetchStudents, isLoading } = useStudentStore();
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  if (selectedStudent) {
    return <StudentDetailView student={selectedStudent} onBack={() => setSelectedStudent(null)} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Students</h1>
          </div>
        </div>
        <div className="text-center py-8 text-muted-foreground">Loading students...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Students</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Students in your units ({students.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <StudentsDataTable
            students={students}
            onStudentClick={setSelectedStudent}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function StudentDetailView({ student, onBack }: { student: User; onBack: () => void }) {
  const [studentAssignments, setStudentAssignments] = useState<Assignment[]>([]);
  const [studentAchievements, setStudentAchievements] = useState<UserAchievement[]>([]);
  const [studentPlayingTimes, setStudentPlayingTimes] = useState<StudentPlayingTime[]>([]);
  const [sharedUnits, setSharedUnits] = useState<Unit[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingPlayingTimes, setLoadingPlayingTimes] = useState(false);

  useEffect(() => {
    const fetchStudentData = async () => {
      setLoading(true);
      setSelectedUnitId('');
      setStudentPlayingTimes([]);
      setSharedUnits([]);
      try {
        const [assignmentsResponse, achievementsResponse, unitsResponse] = await Promise.all([
          apiClient.getStudentGames(student.id),
          apiClient.getAllUserAchievements(student.id),
          apiClient.getMyUnits(),
        ]);

        if (assignmentsResponse.success) {
          setStudentAssignments(assignmentsResponse.data);
        }
        if (achievementsResponse.success) {
          setStudentAchievements(achievementsResponse.data);
        }
        if (unitsResponse.success) {
          const studentUnitIds = new Set(student.unitIds ?? []);
          setSharedUnits(unitsResponse.data.filter(unit => studentUnitIds.has(unit.id)));
        }
      } catch (error) {
        console.error('Failed to load student data:', error);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [student.id, student.unitIds]);

  useEffect(() => {
    if (!selectedUnitId) {
      return;
    }

    const fetchPlayingTimes = async () => {
      setLoadingPlayingTimes(true);
      setStudentPlayingTimes([]);
      try {
        const playingTimesResponse = await apiClient.getStudentPlayingTimes(
          student.id,
          selectedUnitId
        );
        if (playingTimesResponse.success) {
          const transformedPlayingTimes = playingTimesResponse.data.map(pt => ({
            gameId: pt.gameId,
            gameTitle: pt.gameTitle,
            totalMinutes: Math.round(pt.totalPlayingTimeSeconds / 60),
            sessionCount: pt.sessionCount,
            lastPlayedAt: pt.lastPlayedAt,
          }));
          setStudentPlayingTimes(transformedPlayingTimes);
        }
      } catch (error) {
        console.error('Failed to load student playing time:', error);
        Sentry.captureException(error);
      } finally {
        setLoadingPlayingTimes(false);
      }
    };

    fetchPlayingTimes();
  }, [selectedUnitId, student.id]);

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="self-start">
            ← Back
          </Button>
        </div>
        <div className="text-center py-8 text-muted-foreground">Loading student data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" onClick={onBack} className="self-start">
          ← Back
        </Button>
        <div className="flex items-center gap-3 sm:gap-4 flex-1">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-semibold text-[hsl(var(--color-primary-text))] sm:text-2xl">
              {student.firstName.charAt(0)}
            </span>
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{student.firstName} {student.lastName}</h1>
            <p className="text-muted-foreground text-sm sm:text-base truncate">{student.email}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="assignments" className="w-full">
        <TabsList>
          <TabsTrigger value="assignments">Assignments ({studentAssignments.length})</TabsTrigger>
          <TabsTrigger value="achievements">Achievements ({studentAchievements.length})</TabsTrigger>
          <TabsTrigger value="playing-time">Playing Time</TabsTrigger>
        </TabsList>

        <TabsContent value="assignments" className="space-y-4 mt-6">
          {studentAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No assignments yet
              </CardContent>
            </Card>
          ) : (
            studentAssignments.map((assignment) => (
              <Card key={assignment.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{assignment.game?.title}</CardTitle>
                      <CardDescription className="mt-1">
                        {assignment.game?.description}
                      </CardDescription>
                    </div>
                    <Badge variant={assignment.isActive ? 'default' : 'secondary'}>
                      {assignment.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(assignment.startDate).toLocaleDateString()} - {new Date(assignment.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Achievements Unlocked</span>
                      <span className="font-semibold">
                        {studentAchievements.filter(a => a.gameId === assignment.gameId).length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="achievements" className="space-y-4 mt-6">
          {studentAchievements.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No achievements unlocked yet
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-lg border divide-y">
              {studentAchievements.map((userAchievement) => {
                const unlockedAt = new Date(userAchievement.unlockedAt);
                const unlockedLabel = `Unlocked ${unlockedAt.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}, ${unlockedAt.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}`;
                const unit = userAchievement.unit;
                const semesterLabel = unit
                  ? `${SEMESTER_LABELS[unit.semester] ?? unit.semester} ${unit.year}`
                  : null;

                return (
                  <div
                    key={userAchievement.id}
                    className="flex items-start gap-3 sm:gap-4 px-3 sm:px-4 py-3"
                  >
                    {userAchievement.achievement?.unlockedIconUrl ? (
                      <img
                        src={userAchievement.achievement.unlockedIconUrl}
                        alt={userAchievement.achievement?.name}
                        className="w-12 h-12 object-contain flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-6 h-6 text-yellow-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm sm:text-base truncate">
                        {userAchievement.achievement?.name || 'Unknown Achievement'}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {userAchievement.gameTitle || 'Unknown Game'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                        {userAchievement.achievement?.description || 'No description'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{unlockedLabel}</p>
                    </div>
                    {unit && (
                      <div className="text-right flex-shrink-0 max-w-[12rem] sm:max-w-[18rem]">
                        <p className="text-sm font-medium truncate">{unit.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{unit.code}</p>
                        <p className="text-xs text-muted-foreground truncate">{semesterLabel}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="playing-time" className="space-y-4 mt-6">
          <div className="w-full sm:w-64">
            <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a unit" />
              </SelectTrigger>
              <SelectContent>
                {sharedUnits.map(unit => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {sharedUnits.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                This student is not enrolled in one of your units
              </CardContent>
            </Card>
          ) : !selectedUnitId ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Select a unit to view playing time
              </CardContent>
            </Card>
          ) : loadingPlayingTimes ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Loading playing time...
              </CardContent>
            </Card>
          ) : studentPlayingTimes.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No playing time recorded yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {studentPlayingTimes.map((playingTime, index) => {
                const hours = Math.floor(playingTime.totalMinutes / 60);
                const minutes = playingTime.totalMinutes % 60;

                return (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{playingTime.gameTitle}</h4>
                          {playingTime.lastPlayedAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Last played: {new Date(playingTime.lastPlayedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-lg font-semibold">
                              {hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

